import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SimpleChart from '../components/SimpleChart';
import { ArrowRight, BookOpen, Shield, TrendingUp } from 'lucide-react';

const Guides = () => {
  const guides = [
    {
      id: 'choose-casino',
      title: 'How to Choose the Right UK Online Casino',
      description: 'Master the art of selecting a casino that matches your gaming style and preferences.'
    },
    {
      id: 'uk-regulations',
      title: 'Understanding UK Gambling Commission Regulations',
      description: 'Navigate the legal landscape and understand your rights as a UK player.'
    },
    {
      id: 'casino-bonuses',
      title: 'How to Claim and Use Casino Bonuses',
      description: 'Maximize your bankroll with smart bonus strategies and withdrawal techniques.'
    },
    {
      id: 'payment-methods',
      title: 'Payment Methods for UK Players',
      description: 'Compare payment options to find the fastest and most secure deposit methods.'
    },
    {
      id: 'responsible-gambling',
      title: 'Responsible Gambling in the UK',
      description: 'Play safely with expert tips on managing your gaming activity and staying in control.'
    },
    {
      id: 'rtp-odds',
      title: 'Understanding Casino Game RTP and Odds',
      description: 'Learn how return-to-player rates affect your winning potential across different games.'
    },
    {
      id: 'mobile-casino',
      title: 'Mobile Casino Gaming in the UK',
      description: 'Play on the go with our comprehensive guide to mobile casino apps and sites.'
    },
    {
      id: 'live-dealer',
      title: 'Live Dealer Games Explained',
      description: 'Experience the thrill of real-time casino action from your home with live dealer games.'
    },
    {
      id: 'withdrawals',
      title: 'Withdrawal Times and Methods',
      description: 'Get your winnings fast with insights into withdrawal speeds and processing times.'
    }
  ];

  const chart1Data = [
    { label: 'Game Selection', value: 28 },
    { label: 'Bonus Offers', value: 24 },
    { label: 'Payment Speed', value: 22 },
    { label: 'Security & Licensing', value: 26 }
  ];

  const chart2Data = [
    { label: 'E-wallets (PayPal, Skrill)', value: 38, color: '#8d7762', color2: '#615344' },
    { label: 'Debit Cards', value: 35, color: '#b8a893', color2: '#8d7762' },
    { label: 'Bank Transfer', value: 18, color: '#756352', color2: '#574035' },
    { label: 'Cryptocurrency', value: 9, color: '#a8927a', color2: '#7d5a46' }
  ];

  const chart3Data = [
    { label: 'Welcome Bonus', value: 35 },
    { label: 'Free Spins', value: 28 },
    { label: 'Reload Bonuses', value: 20 },
    { label: 'VIP Rewards', value: 17 }
  ];

  return (
    <div className="min-h-screen bg-beige-50">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-brown-900 to-brown-950 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center space-x-2 mb-6 px-4 py-2 glass-dark rounded-full">
                <BookOpen className="w-5 h-5" />
                <span className="font-medium">Complete UK Casino Guide</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                How to Play Online Casinos in UK
              </h1>
              <p className="text-xl text-beige-200 max-w-2xl mx-auto leading-relaxed">
                Everything you need to know to play smart, win more, and stay safe in the UK online casino world.
              </p>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="glass-strong rounded-3xl p-8 md:p-12 mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-brown-900 mb-6">
                How We Write Our Guides
              </h2>
              <div className="space-y-6 text-lg text-brown-700 leading-relaxed">
                <p>
                  Our guides represent more than just information—they're your strategic advantage in the UK online casino market. 
                  Each guide is crafted through extensive research, real-world testing, and deep industry knowledge that spans 
                  decades of experience. We don't just tell you what to do; we show you why it matters and how it impacts your 
                  actual gaming experience.
                </p>
                <p>
                  Every piece of advice you'll find here has been battle-tested by our team of gambling industry professionals 
                  who have seen trends come and go, witnessed regulatory changes, and understand what truly separates excellent 
                  casinos from mediocre ones. We test payment methods personally, analyze bonus terms in detail, and track 
                  withdrawal speeds across multiple operators to give you accurate, actionable insights.
                </p>
                <p>
                  What sets our guides apart is our commitment to transparency. We explain the reasoning behind every recommendation, 
                  helping you build your own expertise rather than just following instructions blindly. When we discuss RTP rates, 
                  we don't just list numbers—we explain how they translate to real money over extended play sessions. When we 
                  cover regulations, we clarify how they protect you and what red flags to watch for.
                </p>
                <p>
                  Our writing style balances accessibility with depth. Whether you're completely new to online casinos or an 
                  experienced player looking to refine your strategy, our guides provide value at every level. We use clear 
                  language without dumbing down complex concepts, and we include practical examples that illustrate key points 
                  in ways that stick with you long after you've finished reading.
                </p>
                <p>
                  Most importantly, every guide is written with your best interests in mind. We don't promote casinos that don't 
                  meet our standards, regardless of commercial relationships. Our editorial independence ensures that every piece 
                  of advice serves one purpose: helping you make smarter decisions that lead to better outcomes. Trust isn't 
                  built overnight, and our guides reflect the careful, methodical approach we take to earning yours.
                </p>
              </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              <SimpleChart
                title="Key Selection Factors"
                data={chart1Data}
                type="bar"
              />
              <SimpleChart
                title="Popular Payment Methods"
                data={chart2Data}
                type="pie"
              />
              <SimpleChart
                title="Most Valued Bonuses"
                data={chart3Data}
                type="bar"
              />
            </div>

            {/* Guides Grid */}
            <div className="mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-brown-900 mb-8 text-center">
                Essential Casino Guides
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {guides.map((guide, index) => (
                  <Link
                    key={guide.id}
                    to={`/guides/${guide.id}`}
                    data-discover="true"
                    className="group glass-strong rounded-2xl p-6 hover:scale-105 transition-transform duration-300 block"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-brown-600 to-brown-800 rounded-lg flex items-center justify-center text-white font-bold text-lg group-hover:scale-110 transition-transform">
                        {index + 1}
                      </div>
                      <ArrowRight className="w-5 h-5 text-brown-700 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </div>
                    <h3 className="text-xl font-bold text-brown-900 mb-3 group-hover:text-brown-700 transition-colors">
                      {guide.title}
                    </h3>
                    <p className="text-brown-700 leading-relaxed">
                      {guide.description}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Guides;
